import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Modal } from '../../components/Modal';
import { can } from '../../auth/store';
import { ApiError } from '../../api/client';
import {
  useUpdateAdminUser,
  useResetUserPassword,
  useForceLogoutUser,
  useRbacRoles,
  type AdminUser,
} from '../../api/queries/settings';
import { Toggle } from '../../components/Toggle';

interface UserDetailModalProps {
  user: AdminUser;
  open: boolean;
  onClose: () => void;
}

function formatWhen(ms: number | null): string {
  if (!ms) return 'Never';
  try {
    return new Date(ms).toLocaleString();
  } catch {
    return '—';
  }
}

/** Admin "open a user entry" surface: edit display name / roles / active state,
 *  reset the password, force logout, and jump to the linked Staff directory
 *  entry. Built entirely on the existing /api/users endpoints (the list row
 *  already carries every field) — no new backend. Remounted per user via a
 *  `key` at the call site, so local state initializes straight from props. */
export function UserDetailModal({ user, open, onClose }: UserDetailModalProps): JSX.Element {
  const update = useUpdateAdminUser();
  const reset = useResetUserPassword();
  const forceLogout = useForceLogoutUser();
  const rolesQuery = useRbacRoles();
  const allRoleNames = (rolesQuery.data?.roles ?? []).map((r) => r.name);

  const [displayName, setDisplayName] = useState(user.display_name);
  const [isActive, setIsActive] = useState(user.is_active);
  const [roles, setRoles] = useState<string[]>(user.role_names);
  const [saveErr, setSaveErr] = useState<string | null>(null);

  const [resetPw, setResetPw] = useState('');
  const [resetMsg, setResetMsg] = useState<string | null>(null);
  const [resetErr, setResetErr] = useState<string | null>(null);

  const [logoutMsg, setLogoutMsg] = useState<string | null>(null);

  const canLinkDirectory = user.person_id !== null && can('organization.read');

  const toggleRole = (rn: string) => {
    setRoles((prev) => (prev.includes(rn) ? prev.filter((r) => r !== rn) : [...prev, rn]));
  };

  const onSave = async () => {
    setSaveErr(null);
    if (displayName.trim().length === 0) { setSaveErr('Display name is required.'); return; }
    if (roles.length === 0) { setSaveErr('Pick at least one role.'); return; }
    try {
      await update.mutateAsync({
        id: user.id,
        display_name: displayName.trim(),
        is_active: isActive,
        role_names: roles,
      });
      onClose();
    } catch (err) {
      setSaveErr(err instanceof ApiError ? err.message : 'Could not save the user.');
    }
  };

  const onResetPw = async (e: FormEvent) => {
    e.preventDefault();
    setResetErr(null);
    setResetMsg(null);
    if (resetPw.length < 12) { setResetErr('Password ≥ 12 chars'); return; }
    try {
      await reset.mutateAsync({ id: user.id, new_password: resetPw });
      setResetPw('');
      setResetMsg('Password reset — the user is signed out everywhere and must use the new password.');
    } catch {
      setResetErr('Reset failed.');
    }
  };

  const onForceLogout = () => {
    if (!confirm(`Revoke all sessions for ${user.email}? They will be signed out everywhere.`)) return;
    setLogoutMsg(null);
    forceLogout.mutate(user.id, {
      onSuccess: (resp) => setLogoutMsg(`Revoked ${resp.revoked} session(s).`),
      onError: () => setLogoutMsg('Force logout failed.'),
    });
  };

  const footer = (
    <>
      <button
        type="button"
        className="btn-charcoal-secondary set-link-warn"
        onClick={onForceLogout}
        disabled={forceLogout.isPending}
      >
        Force logout
      </button>
      <div style={{ flex: 1 }} />
      <button type="button" className="btn-charcoal-secondary" onClick={onClose} disabled={update.isPending}>
        Cancel
      </button>
      <button type="button" className="btn-cyan-primary" onClick={onSave} disabled={update.isPending}>
        {update.isPending ? 'Saving…' : 'Save'}
      </button>
    </>
  );

  return (
    <Modal title={user.display_name} open={open} onClose={onClose} busy={update.isPending} footer={footer}>
      <div className="set-form">
        <div className="udm-meta">
          <div>
            <div className="set-field-label">Email</div>
            <div className="udm-value">{user.email}</div>
          </div>
          <div>
            <div className="set-field-label">2FA</div>
            <div className="udm-value">
              {user.is_2fa_enabled
                ? <span className="set-pill set-pill-ok">ON</span>
                : <span className="set-pill">OFF</span>}
            </div>
          </div>
          <div>
            <div className="set-field-label">Last login</div>
            <div className="udm-value">{formatWhen(user.last_login_at)}</div>
          </div>
          <div>
            <div className="set-field-label">Staff directory</div>
            <div className="udm-value">
              {canLinkDirectory
                ? <Link to={`/people/${user.person_id}`} className="set-link">View in directory ↗</Link>
                : <span className="set-muted">—</span>}
            </div>
          </div>
        </div>

        <label className="set-field">
          <span>Display name</span>
          <input
            type="text"
            className="form-input"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            maxLength={200}
          />
        </label>

        <div className="set-field">
          <span>Roles</span>
          <div className="set-role-toggles">
            {allRoleNames.map((rn) => (
              <Toggle
                key={rn}
                checked={roles.includes(rn)}
                onChange={() => toggleRole(rn)}
                label={rn}
                size="sm"
              />
            ))}
          </div>
        </div>

        <Toggle
          checked={isActive}
          onChange={setIsActive}
          label="Active (can sign in)"
        />

        {saveErr && <p className="set-error" role="alert">{saveErr}</p>}

        <form className="udm-reset" onSubmit={onResetPw}>
          <div className="set-field-label">Reset password</div>
          <div className="udm-reset-row">
            <input
              type="text"
              className="form-input"
              placeholder="New password (≥12 chars)"
              value={resetPw}
              onChange={(e) => setResetPw(e.target.value)}
              minLength={12}
            />
            <button type="submit" className="btn-charcoal-secondary" disabled={reset.isPending || resetPw.length < 12}>
              {reset.isPending ? 'Resetting…' : 'Reset'}
            </button>
          </div>
          {resetErr && <p className="set-error" role="alert">{resetErr}</p>}
          {resetMsg && <p className="set-banner" role="status">{resetMsg}</p>}
        </form>

        {logoutMsg && <p className="set-banner" role="status">{logoutMsg}</p>}
      </div>
    </Modal>
  );
}
